/**
 * Flag de teste dos módulos novos (Portal de Cotações e Orçamento Cilia) em produção.
 *
 * Com `REACT_APP_MODULOS_NOVOS_SOMENTE_ADMIN=true`, TODA superfície dos dois módulos
 * passa a exigir ADMIN (sem TI — decisão do Victor, 20/08/2026): rotas, itens de menu,
 * abas da Auditoria, cartões do hub de Administração, badges e sockets. Remover a env
 * devolve os papéis normais sem tocar código.
 *
 * ⚠️ É gate de UI, não de segurança: os backends dos módulos continuam autorizando os
 * papéis normais por API — mesmo princípio do `useIsTi`/`deliveredMode` (ADR 0012).
 *
 * As duas são FUNÇÕES lidas na hora do uso, nunca constantes de import (lição da at-73
 * e da at-05 do Portal): é o que permite aos testes controlar a flag por `process.env`
 * sem depender do valor congelado no primeiro import. No build de produção o webpack
 * substitui a expressão pelo literal — o custo em runtime é zero.
 */
export function modulosNovosSomenteAdmin(): boolean {
  return process.env.REACT_APP_MODULOS_NOVOS_SOMENTE_ADMIN === 'true';
}

/** Os módulos que a flag alcança. Uma superfície pertence a exatamente um deles. */
export type ModuloNovo = 'portal-cotacoes' | 'orcamento-cilia' | 'estoque' | 'previsao-pecas';

/**
 * Módulos que JÁ tiveram go-live: a flag deixa de alcançá-los, e eles voltam aos papéis normais.
 *
 * 🔴 **Esta lista é a definição ÚNICA de "quem já saiu do modo teste"** (22/09/2026). Antes da
 * liberação do Cilia a alternativa era remover `papeisModuloNovo` de cada superfície dele — 6
 * arquivos, cada um decidindo por conta própria —, e a segunda liberação divergiria da primeira
 * em algum ponto esquecido, que apareceria como módulo visível para quem não devia.
 *
 * ⚠️ O go-live de TODOS de uma vez continua sendo remover a env do workflow de deploy; esta lista
 * é para liberar um módulo antes dos outros.
 */
const MODULOS_LIBERADOS: ModuloNovo[] = [
  // Requisições (Orçamento Cilia) — decisão do Victor, 22/09/2026: o usuário ORCAMENTO precisa
  // criar requisição no Nasajon, e o módulo estava invisível em produção só por causa da flag.
  'orcamento-cilia',
  // Portal de Cotações — decisão do Victor, 28/10/2026 (deploy da v1.47.0): sai do modo teste
  // junto com o envio real de pedidos ao Nasajon. A Previsão de Peças segue em teste (só ADMIN).
  'portal-cotacoes',
];

/**
 * A flag ainda alcança esta superfície?
 *
 * ⚠️ **Sem `modulo`, responde como sempre respondeu** (a flag alcança tudo). O parâmetro é
 * opcional de propósito, e o default é o seguro: chamada nova que esqueça de se identificar
 * continua restrita ao ADMIN, em vez de escapar da flag em silêncio.
 */
export function moduloEmModoTeste(modulo?: ModuloNovo): boolean {
  if (!modulosNovosSomenteAdmin()) return false;
  return modulo === undefined || !MODULOS_LIBERADOS.includes(modulo);
}

/** Papéis efetivos de uma superfície de módulo novo: `['ADMIN']` em teste, o padrão fora dele. */
export function papeisModuloNovo(padrao: string[], modulo?: ModuloNovo): string[] {
  return moduloEmModoTeste(modulo) ? ['ADMIN'] : padrao;
}
